/**
 * SpeedometerSection — Interactive EV/EBITDA sensitivity with sliders and animated gauge.
 *
 * Calls POST /sensitivity/enhanced/combined/calculateSpeedometerSensitivityAnalysis
 * with debounced slider values (500 ms). Gauge animates to the new ev_ebitda_change_percent.
 *
 * UI Structure:
 *   1. Header row (title + year selector)
 *   2. 4 slider cards: Revenue, EBITDA, Invested Capital, WACC (-10% → +10%)
 *   3. Animated speedometer gauge (SVG, -50% → +50%)
 *   4. Status bar (current adjustments + EV + EV/EBITDA change)
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { useSpeedometerSensitivity } from '../hooks/useSensitivityAnalysis';
import { useSpeedometerStore } from '../../../stores/speedometerStore';
import { formatCurrencyValue } from '../utils/formatters';
import type { RegionConfig, SpeedometerSensitivityResponse } from '../../../types/financial';

// ── Speedometer SVG ───────────────────────────────────────────────────────────

const GAUGE_SIZE = 420;
const GAUGE_RADIUS = GAUGE_SIZE / 2 - 24;
const CX = GAUGE_SIZE / 2;
const CY = GAUGE_SIZE / 2;

function arcPath(startDeg: number, endDeg: number): string {
  const s = (startDeg * Math.PI) / 180;
  const e = (endDeg * Math.PI) / 180;
  const x1 = CX + GAUGE_RADIUS * Math.cos(s);
  const y1 = CY + GAUGE_RADIUS * Math.sin(s);
  const x2 = CX + GAUGE_RADIUS * Math.cos(e);
  const y2 = CY + GAUGE_RADIUS * Math.sin(e);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${x1} ${y1} A ${GAUGE_RADIUS} ${GAUGE_RADIUS} 0 ${large} 1 ${x2} ${y2}`;
}

const ARC_SEGMENTS = [
  { from: 180, to: 216, stroke: '#b71c1c' },
  { from: 216, to: 252, stroke: '#d32f2f' },
  { from: 252, to: 270, stroke: '#ef5350' },
  { from: 270, to: 288, stroke: '#9e9e9e' },
  { from: 288, to: 306, stroke: '#66bb6a' },
  { from: 306, to: 342, stroke: '#2e7d32' },
  { from: 342, to: 360, stroke: '#1b5e20' },
];

function gaugeColor(val: number): string {
  if (val > 30) return '#1b5e20';
  if (val > 15) return '#2e7d32';
  if (val > 5) return '#66bb6a';
  if (val > -5) return '#475569'; // slate-600 — visible on white at neutral
  if (val > -15) return '#ef5350';
  if (val > -30) return '#d32f2f';
  return '#b71c1c';
}

function SpeedometerGauge({
  value,
  loading,
}: {
  value: number;
  loading: boolean;
}) {
  const [animated, setAnimated] = useState(value);
  const rafRef = useRef<number>();

  useEffect(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const start = animated;
    const end = value;
    const t0 = performance.now();
    const duration = 800;

    const step = (now: number) => {
      const p = Math.min((now - t0) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setAnimated(start + (end - start) * ease);
      if (p < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const norm = Math.max(0, Math.min(1, (animated - (-50)) / 100));
  const angle = 180 + norm * 180;
  const color = gaugeColor(value);

  // Needle tip
  const nx = CX + (GAUGE_RADIUS - 10) * Math.cos((angle * Math.PI) / 180);
  const ny = CY + (GAUGE_RADIUS - 10) * Math.sin((angle * Math.PI) / 180);
  // Needle tail — capped at 16px so it never reaches the label zone below the hub
  const tailAngle = angle + 180;
  const tx = CX + 16 * Math.cos((tailAngle * Math.PI) / 180);
  const ty = CY + 16 * Math.sin((tailAngle * Math.PI) / 180);

  // SVG height: enough room for arc + hub + labels below (CY+48)
  const svgH = CY + 48;

  return (
    <div className="flex flex-col items-center gap-1">
      <svg
        width={GAUGE_SIZE}
        height={svgH}
        viewBox={`0 0 ${GAUGE_SIZE} ${svgH}`}
      >
        {/* Arc segments */}
        {ARC_SEGMENTS.map((seg, i) => (
          <path
            key={i}
            d={arcPath(seg.from, seg.to)}
            fill="none"
            stroke={seg.stroke}
            strokeWidth={16}
            strokeLinecap="round"
          />
        ))}

        {/* 0% tick mark */}
        <line
          x1={CX + (GAUGE_RADIUS - 20) * Math.cos((270 * Math.PI) / 180)}
          y1={CY + (GAUGE_RADIUS - 20) * Math.sin((270 * Math.PI) / 180)}
          x2={CX + (GAUGE_RADIUS + 8) * Math.cos((270 * Math.PI) / 180)}
          y2={CY + (GAUGE_RADIUS + 8) * Math.sin((270 * Math.PI) / 180)}
          stroke="#94a3b8"
          strokeWidth={2.5}
          strokeLinecap="round"
        />

        {/* Needle (tip + tail) */}
        <line
          x1={tx} y1={ty} x2={nx} y2={ny}
          stroke={color}
          strokeWidth={4}
          strokeLinecap="round"
          style={{ transition: 'none' }}
        />

        {/* Center hub */}
        <circle cx={CX} cy={CY} r={11} fill={color} stroke="white" strokeWidth={3} />
        <circle cx={CX} cy={CY} r={4} fill="white" />

        {/* Side labels — positioned below arc endpoints so they don't overlap the arc */}
        <text x={12} y={CY + 22} fill="#6b7280" fontSize={12} fontWeight={700}>-50%</text>
        <text x={GAUGE_SIZE - 52} y={CY + 22} fill="#6b7280" fontSize={12} fontWeight={700}>+50%</text>
        {/* Center "0%" label — well clear of needle tail */}
        <text x={CX} y={CY + 40} fill="#94a3b8" fontSize={11} fontWeight={600} textAnchor="middle">0%</text>
      </svg>

      {/* Value display — below the SVG, no overlap */}
      <div className="h-10 flex items-center justify-center">
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin text-violet-500" />
        ) : (
          <p
            className="text-3xl font-bold tabular-nums text-center"
            style={{ color, minWidth: 120 }}
          >
            {animated >= 0 ? '+' : ''}{animated.toFixed(2)}%
          </p>
        )}
      </div>
    </div>
  );
}

// ── MetricSliderCard ──────────────────────────────────────────────────────────

function MetricSliderCard({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  const pct = ((value + 10) / 20) * 100; // 0% @ −10, 50% @ 0, 100% @ +10
  const center = 50;
  // Bidirectional fill from center: violet right of 0, red left of 0, gray otherwise
  const trackColor =
    value > 0
      ? `linear-gradient(to right, #e5e7eb 0%, #e5e7eb ${center}%, #7c3aed ${center}%, #7c3aed ${pct}%, #e5e7eb ${pct}%, #e5e7eb 100%)`
      : value < 0
      ? `linear-gradient(to right, #e5e7eb 0%, #e5e7eb ${pct}%, #ef4444 ${pct}%, #ef4444 ${center}%, #e5e7eb ${center}%, #e5e7eb 100%)`
      : '#e5e7eb';

  return (
    <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-4 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-neutral-700">{label}</span>
        <span
          className={`text-sm font-bold tabular-nums px-2.5 py-0.5 rounded-lg ${
            value > 0
              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
              : value < 0
              ? 'bg-red-50 text-red-700 border border-red-200'
              : 'bg-neutral-100 text-neutral-600 border border-neutral-200'
          }`}
        >
          {value > 0 ? '+' : ''}{value.toFixed(1)}%
        </span>
      </div>
      <input
        type="range"
        min={-10}
        max={10}
        step={0.1}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-1.5 rounded-full appearance-none cursor-pointer focus:outline-none"
        style={{ background: trackColor }}
      />
      <div className="flex justify-between mt-2">
        <span className="text-[11px] text-neutral-400">-10%</span>
        <span className="text-[11px] text-neutral-400">0%</span>
        <span className="text-[11px] text-neutral-400">+10%</span>
      </div>
    </div>
  );
}

// ── Main SpeedometerSection ───────────────────────────────────────────────────

interface Props {
  companyName: string;
  region: RegionConfig;
}

export default function SpeedometerSection({ companyName, region }: Props) {
  // ── Zustand store for caching ────────────────────────────────────────────
  const { getCached, setCache } = useSpeedometerStore();

  // Restore from cache (if returning to this tab for the same company)
  const cached = getCached(companyName);

  const [revenue, setRevenue] = useState(cached?.sliders.revenue ?? 0);
  const [ebitda, setEbitda] = useState(cached?.sliders.ebitda ?? 0);
  const [investedCapital, setInvestedCapital] = useState(cached?.sliders.investedCapital ?? 0);
  const [wacc, setWacc] = useState(cached?.sliders.wacc ?? 0);
  const [selectedYear, setSelectedYear] = useState<number | string | null>(cached?.selectedYear ?? null);
  const [localData, setLocalData] = useState<SpeedometerSensitivityResponse | null>(cached?.data ?? null);

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const mountedRef = useRef(false);
  const { mutate, isPending } = useSpeedometerSensitivity();

  // Helper: extract the latest numeric year from a response
  const getLatestYear = (res: SpeedometerSensitivityResponse): number | string => {
    const realYears = res.ev_ebitda_data
      .map((d) => d.financial_year)
      .filter((y): y is number => typeof y === 'number');
    return realYears.length > 0 ? Math.max(...realYears) : res.ev_ebitda_data[0].financial_year;
  };

  const runAnalysis = useCallback(() => {
    mutate(
      { company: companyName, revenue, ebitda, invested_capital: investedCapital, wacc },
      {
        onSuccess: (res) => {
          setLocalData(res);
          // Always default to latest year if none selected yet
          const year = selectedYear ?? getLatestYear(res);
          setSelectedYear(year);
          // Persist to store
          setCache(companyName, {
            data: res,
            selectedYear: year,
            sliders: { revenue, ebitda, investedCapital, wacc },
          });
        },
      }
    );
  }, [companyName, revenue, ebitda, investedCapital, wacc, selectedYear, mutate, setCache]);

  // On mount: call API with defaults (0% sliders) if no cached data.
  // NOTE: mountedRef is intentionally NOT set here — it's owned by the slider
  // effect below so that effect can skip its own initial run. Setting it here
  // (before the slider effect first runs) let the slider effect fire a second
  // runAnalysis() on mount → duplicate calculateSpeedometerSensitivity call.
  useEffect(() => {
    if (!cached) {
      runAnalysis();
    }
  }, [companyName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced re-run when sliders change. Skip this effect's OWN first
  // invocation (the mount) so only the on-mount effect above fires the initial
  // analysis; subsequent slider changes debounce a single call each.
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runAnalysis(), 500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [revenue, ebitda, investedCapital, wacc]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist selectedYear changes to store
  useEffect(() => {
    if (localData && selectedYear != null) {
      setCache(companyName, {
        data: localData,
        selectedYear,
        sliders: { revenue, ebitda, investedCapital, wacc },
      });
    }
  }, [selectedYear]); // eslint-disable-line react-hooks/exhaustive-deps

  const availableYears = localData?.ev_ebitda_data?.map((d) => d.financial_year) ?? [];

  const yearData =
    localData?.ev_ebitda_data?.find((d) => d.financial_year === selectedYear) ??
    localData?.ev_ebitda_data?.[0];

  const evEbitdaChangePct = yearData?.ev_ebitda_change_percent ?? 0;

  return (
    <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm overflow-hidden">
      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="px-6 py-4 bg-gradient-to-r from-slate-900 to-slate-800 flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-white">Sensitivity Analysis</h3>
          <p className="text-sm text-slate-400 mt-0.5">
            Adjust key financial metrics to see the impact on EV/EBITDA ratio
          </p>
        </div>

        {availableYears.length > 0 && (
          <div className="flex flex-col gap-1 shrink-0">
            <label className="text-xs font-medium text-slate-400">Financial Year</label>
            <select
              value={String(selectedYear ?? '')}
              onChange={(e) => {
                const raw = e.target.value;
                const num = Number(raw);
                setSelectedYear(isNaN(num) ? raw : num);
              }}
              className="px-3 py-1.5 text-sm rounded-lg bg-slate-700 border border-slate-600 text-white focus:outline-none focus:ring-2 focus:ring-violet-500 min-w-[140px]"
            >
              {availableYears.map((yr) => (
                <option key={String(yr)} value={String(yr)}>
                  {yr}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="p-6 space-y-6">
        {/* ── Sliders ─────────────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <MetricSliderCard label="Revenue" value={revenue} onChange={setRevenue} />
          <MetricSliderCard label="EBITDA" value={ebitda} onChange={setEbitda} />
          <MetricSliderCard label="Invested Capital" value={investedCapital} onChange={setInvestedCapital} />
          <MetricSliderCard label="WACC" value={wacc} onChange={setWacc} />
        </div>

        {/* ── Gauge ───────────────────────────────────────────────────────── */}
        <div className="flex justify-center">
          <div className="bg-neutral-50 rounded-2xl border border-neutral-200 px-8 py-6 w-full max-w-lg">
            <p className="text-sm font-semibold text-neutral-700 text-center mb-1">EV/EBITDA Change</p>
            <SpeedometerGauge value={evEbitdaChangePct} loading={isPending} />
          </div>
        </div>

        {/* ── Status bar ──────────────────────────────────────────────────── */}
        <div className="rounded-xl bg-violet-50 border border-violet-100 px-5 py-3.5 space-y-1">
          <p className="text-xs font-semibold text-neutral-700">
            Current Adjustments: Revenue {revenue >= 0 ? '+' : ''}{revenue.toFixed(1)}%,{' '}
            EBITDA {ebitda >= 0 ? '+' : ''}{ebitda.toFixed(1)}%,{' '}
            Invested Capital {investedCapital >= 0 ? '+' : ''}{investedCapital.toFixed(1)}%,{' '}
            WACC {wacc >= 0 ? '+' : ''}{wacc.toFixed(1)}%
          </p>
          {localData && (
            <p className="text-xs text-neutral-500">
              Analyzing {localData.years_analyzed} years of data for {companyName}
            </p>
          )}
          {yearData && (
            <p className="text-xs text-neutral-500">
              EV: {formatCurrencyValue(yearData.ev, region)} | EV/EBITDA Change:{' '}
              <span className={`font-semibold ${evEbitdaChangePct >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {evEbitdaChangePct >= 0 ? '+' : ''}{evEbitdaChangePct.toFixed(2)}%
              </span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
